import React, { useEffect, useState, useRef } from "react"; // Added useRef
import { motion, AnimatePresence } from "framer-motion";
import { FaPlane, FaTimes, FaChevronDown } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EmailModal from "./EmailModal";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2;

// Country codes data
const countryCodes = [
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+1", country: "USA", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+966", country: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+82", country: "South Korea", flag: "🇰🇷" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+7", country: "Russia", flag: "🇷🇺" },
  { code: "+55", country: "Brazil", flag: "🇧🇷" },
  { code: "+27", country: "South Africa", flag: "🇿🇦" },
  { code: "+20", country: "Egypt", flag: "🇪🇬" },
  { code: "+92", country: "Pakistan", flag: "🇵🇰" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
  { code: "+977", country: "Nepal", flag: "🇳🇵" },
  { code: "+975", country: "Bhutan", flag: "🇧🇹" },
  { code: "+95", country: "Myanmar", flag: "🇲🇲" },
  { code: "+855", country: "Cambodia", flag: "🇰🇭" },
  { code: "+856", country: "Laos", flag: "🇱🇦" },
  { code: "+66", country: "Thailand", flag: "🇹🇭" },
  { code: "+84", country: "Vietnam", flag: "🇻🇳" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩" },
  { code: "+63", country: "Philippines", flag: "🇵🇭" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿" },
];

const LoginModal = ({
  isOpen,
  onClose,
  onLoginSuccess,
  setCheckModelLogin,
  setUser,
}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "", // Added confirm password field
    phone: "", // This will store full phone number with country code
    gender: "",
    hometowncity: "",
    DOB: "",
    Aniversary: "",
    refrence: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [showCountryCodes, setShowCountryCodes] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]); // Default to India
  const [phoneNumber, setPhoneNumber] = useState(""); // Stores only the number part

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  // Add ref for the dropdown
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryCodes(false);
      }
    };

    // Add event listener when dropdown is open
    if (showCountryCodes) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCountryCodes]); // Only re-run when showCountryCodes changes

  // Toast configuration
  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === "error") {
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (type === "info") {
      toast.info(message, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Validation rules
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "username":
        if (!value.trim() && !isLogin) error = "Username is required";
        else if (!isLogin && value.length < 2)
          error = "Username must be at least 2 characters";
        break;

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) error = "Email is required";
        else if (!emailRegex.test(value))
          error = "Please enter a valid email address";
        break;

      case "password":
        if (!value) error = "Password is required";
        else if (value.length < 8)
          error = "Password must be at least 8 characters";
        else if (!isLogin && !/(?=.*[A-Za-z])(?=.*\d)/.test(value))
          error = "Password must contain letters and numbers";
        break;

      case "confirmPassword":
        if (!value && !isLogin) error = "Please confirm your password";
        else if (!isLogin && value !== formData.password)
          error = "Passwords do not match";
        break;

      case "phone":
        if (!value && !isLogin) error = "Phone number is required";
        else if (value) {
          // Remove country code for validation
          const phoneWithoutCode = value
            .replace(selectedCountry.code, "")
            .trim();
          if (!phoneWithoutCode) error = "Phone number is required";
          else if (!/^\d+$/.test(phoneWithoutCode))
            error = "Phone number must contain only digits";
          else if (phoneWithoutCode.length !== 10)
            error = "Phone number must be exactly 10 digits";
        }
        break;

      case "gender":
        if (!value && !isLogin) error = "Gender is required";
        break;

      case "hometowncity":
        if (!value.trim() && !isLogin) error = "City is required";
        break;

      case "DOB":
        if (!value && !isLogin) error = "Date of birth is required";
        else if (value) {
          const dob = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          if (age < 13) error = "You must be at least 13 years old";
          if (dob > today) error = "Date of birth cannot be in the future";
        }
        break;

      case "Aniversary":
        if (value) {
          const anniversary = new Date(value);
          const dob = new Date(formData.DOB);
          const today = new Date();
          if (anniversary < dob)
            error = "Anniversary cannot be before date of birth";
          if (anniversary > today)
            error = "Anniversary cannot be in the future";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = isLogin
      ? ["email", "password"]
      : [
          "username",
          "email",
          "password",
          "confirmPassword",
          "phone",
          "gender",
          "hometowncity",
          "DOB",
        ];

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (isOpen) {
      window.scrollTo(0, 0);
      // Reset form when modal opens
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        phone: "",
        gender: "",
        hometowncity: "",
        DOB: "",
        Aniversary: "",
        refrence: "",
      });
      setPhoneNumber("");
      setSelectedCountry(countryCodes[0]);
      setErrors({});
      setTouched({});
      setIsLogin(true);
    }
  }, [isOpen]);

  const handleBlur = (e) => {
    const { name } = e.target;

    if (name === "phone") {
      setTouched((prev) => ({ ...prev, [name]: true }));

      // Validate the full phone number
      const fullPhoneNumber = selectedCountry.code + phoneNumber;
      const error = validateField(name, fullPhoneNumber);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    } else {
      setTouched((prev) => ({ ...prev, [name]: true }));

      const error = validateField(name, formData[name]);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      // Only allow numbers and limit to 10 digits
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setPhoneNumber(numericValue);

      // Update formData with full phone number including country code
      const fullPhoneNumber = selectedCountry.code + numericValue;
      setFormData((prev) => ({ ...prev, phone: fullPhoneNumber }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Special case: if password changes, also validate confirm password if it's been touched
      if (name === "password" && touched.confirmPassword) {
        const confirmError = validateField(
          "confirmPassword",
          formData.confirmPassword
        );
        setErrors((prev) => ({
          ...prev,
          confirmPassword: confirmError,
        }));
      }
    }

    // Clear error when user starts typing
    if (touched[name]) {
      const error = validateField(
        name,
        name === "phone" ? selectedCountry.code + value : value
      );
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleCountryCodeChange = (country) => {
    setSelectedCountry(country);
    setShowCountryCodes(false);

    // Update formData with new country code
    const fullPhoneNumber = country.code + phoneNumber;
    setFormData((prev) => ({ ...prev, phone: fullPhoneNumber }));

    // Validate phone field if it has been touched
    if (touched.phone) {
      const error = validateField("phone", fullPhoneNumber);
      setErrors((prev) => ({
        ...prev,
        phone: error,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched for validation
    const fieldsToTouch = isLogin
      ? ["email", "password"]
      : [
          "username",
          "email",
          "password",
          "confirmPassword",
          "phone",
          "gender",
          "hometowncity",
          "DOB",
        ];

    const touchedObj = {};
    fieldsToTouch.forEach((field) => {
      touchedObj[field] = true;
    });
    setTouched(touchedObj);

    // Validate form
    // if (!validateForm()) {
    //   showToast("Please fix the form errors before submitting", "error");
    //   return;
    // }

    setLoading(true);
    setErrors({});

    try {
      const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";

      // Prepare payload based on login or register
      let payload;
      if (isLogin) {
        // Login: use email and password
        payload = {
          email: formData.email,
          password: formData.password,
        };
      } else {
        // Register: include all fields except confirmPassword (backend doesn't need it)
        const { confirmPassword, ...registerData } = formData;
        payload = registerData;
      }

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        if (isLogin) {
          showToast("Login successful! ✈️", "success");
          setCheckModelLogin();
          setUser();
          onClose();
          if (onLoginSuccess) onLoginSuccess(data);
        } else {
          showToast(
            "Registration successful! ✅ Please verify your email.",
            "success"
          );

          // Store the email and show verification modal
          setRegisteredEmail(formData.email);
          setShowEmailModal(true);

          // Reset form but don't close modal yet
          setIsLogin(true);
          setFormData({
            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            phone: "",
            gender: "",
            hometowncity: "",
            DOB: "",
            Aniversary: "",
            refrence: "",
          });
          setPhoneNumber("");
          setSelectedCountry(countryCodes[0]);
          setErrors({});
          setTouched({});
        }
      } else {
        const errorMsg =
          data.message ||
          (isLogin ? "Invalid credentials" : "Registration failed");
        setErrors({ submit: errorMsg });
        showToast(errorMsg, "error");
      }
    } catch (error) {
      console.error("Auth error:", error);
      const errorMsg =
        "Network error. Please check your connection and try again.";
      setErrors({ submit: errorMsg });
      showToast(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle verification success
  const handleVerificationSuccess = () => {
    setShowEmailModal(false);
    showToast("Email verified successfully! ✅ You can now login.", "success");
  };

  // Helper function to render input with validation
  const renderInput = (
    field,
    label,
    type = "text",
    placeholder = "",
    options = []
  ) => (
    <motion.div
      key={field}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
    >
      <label className="block text-gray-700 font-medium mb-2">
        {label}{" "}
        {!isLogin && field !== "refrence" && field !== "Aniversary" && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>
      {type === "select" ? (
        <select
          name={field}
          value={formData[field]}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-full px-4 py-3 rounded-lg outline-none transition-all ${
            errors[field] && touched[field]
              ? "border-2 border-red-500 focus:ring-2 focus:ring-red-400"
              : "border-2 border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
          }`}
          required={!isLogin && field !== "refrence" && field !== "Aniversary"}
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={field}
          value={formData[field]}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full px-4 py-3 rounded-lg outline-none transition-all ${
            errors[field] && touched[field]
              ? "border-2 border-red-500 focus:ring-2 focus:ring-red-400"
              : "border-2 border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
          }`}
          required={!isLogin && field !== "refrence" && field !== "Aniversary"}
          minLength={
            field === "password" || field === "confirmPassword" ? 8 : undefined
          }
          maxLength={field === "phone" ? 10 : undefined}
        />
      )}
      {errors[field] && touched[field] && (
        <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
      )}
      {field === "password" && !isLogin && !errors[field] && (
        <p className="mt-1 text-xs text-gray-500">
          Password must be at least 8 characters with letters and numbers
        </p>
      )}
    </motion.div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative 
             max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <FaTimes className="w-5 h-5" />
        </button>

        {/* Header with Gradient Background */}
        <div className="bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <motion.div
              initial={{ rotate: -10 }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FaPlane className="w-10 h-10" />
            </motion.div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-2">
            {isLogin ? "Welcome Back ✈️" : "Join the Sky Club ☁️"}
          </h2>
          <p className="text-center text-blue-100 text-sm">
            {isLogin
              ? "Login to continue booking your dream flight!"
              : "Create an account to start your journey."}
          </p>
        </div>

        {/* Form Section */}
        <div className="p-6 overflow-auto">
          {errors.submit && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm"
            >
              {errors.submit}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <AnimatePresence mode="wait">
              {/* Login Fields: Only Email and Password */}
              {isLogin ? (
                <>
                  {renderInput(
                    "email",
                    "Email",
                    "email",
                    "info@celestialtours.co.in"
                  )}

                  {renderInput("password", "Password", "password", "••••••••")}
                </>
              ) : (
                /* Registration Fields: All fields */
                <>
                  {/* Username Field - Only for Registration */}
                  {renderInput(
                    "username",
                    "Full Name",
                    "text",
                    "Celestial Tours"
                  )}

                  {renderInput(
                    "email",
                    "Email",
                    "email",
                    "info@celestialtours.co.in"
                  )}

                  {/* Phone Input with Country Code Dropdown */}
                  <motion.div
                    key="phone"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    ref={dropdownRef} // Add ref here
                  >
                    <label className="block text-gray-700 font-medium mb-2">
                      Phone Number <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex gap-2">
                      {/* Country Code Dropdown */}
                      <div className="relative w-2/5">
                        <button
                          type="button"
                          onClick={() => setShowCountryCodes(!showCountryCodes)}
                          className={`w-full px-3 py-2.5 border-2 rounded-lg flex items-center justify-between transition-all ${
                            errors.phone && touched.phone
                              ? "border-red-500 focus:ring-2 focus:ring-red-400"
                              : "border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {selectedCountry.flag}
                            </span>
                            <span className="font-medium">
                              {selectedCountry.code}
                            </span>
                          </div>
                          <FaChevronDown
                            className={`w-3 h-3 transition-transform ${
                              showCountryCodes ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {/* Country Codes Dropdown Menu */}
                        <AnimatePresence>
                          {showCountryCodes && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                            >
                              {countryCodes.map((country) => (
                                <button
                                  key={`${country.code}-${country.country}`}
                                  type="button"
                                  onClick={() =>
                                    handleCountryCodeChange(country)
                                  }
                                  className="w-full px-2 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span className="text-lg">
                                    {country.flag}
                                  </span>
                                  <span className="font-medium">
                                    {country.code}
                                  </span>
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Phone Number Input */}
                      <div className="w-3/5">
                        <input
                          type="tel"
                          name="phone"
                          value={phoneNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="9876543210"
                          className={`w-full px-4 py-3 border-2 rounded-lg outline-none transition-all ${
                            errors.phone && touched.phone
                              ? "border-red-500 focus:ring-2 focus:ring-red-400"
                              : "border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                          }`}
                          required={!isLogin}
                          maxLength={10}
                          pattern="\d{10}"
                          title="Please enter exactly 10 digits"
                        />
                      </div>
                    </div>
                    {errors.phone && touched.phone && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.phone}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Full number: {selectedCountry.code}{" "}
                      {phoneNumber || "Your number"}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Phone number must be exactly 10 digits
                    </p>
                  </motion.div>

                  {renderInput("gender", "Gender", "select", "", [
                    
                    { value: "M", label: "Male" },
                    { value: "F", label: "Female" },
                    { value: "O", label: "Other" },
                  ])}

                  {renderInput(
                    "hometowncity",
                    "City",
                    "text",
                    "Enter your city"
                  )}

                  {renderInput("DOB", "Date of Birth", "date")}

                  {renderInput("Aniversary", "Anniversary (optional)", "date")}

                  {/* Password and Confirm Password for Registration */}
                  {renderInput("password", "Password", "password", "••••••••")}

                  {/* Confirm Password Field - Only for Registration */}
                  {renderInput(
                    "confirmPassword",
                    "Confirm Password",
                    "password",
                    "••••••••"
                  )}

                  <motion.div
                    key="refrence"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-gray-700 font-medium mb-2">
                      Referral Code (optional)
                    </label>
                    <p className="text-xs text-blue-600 mb-2">
                      Enter referral code for exclusive benefits
                    </p>
                    <input
                      type="text"
                      name="refrence"
                      value={formData.refrence}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter referral code"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none transition-all"
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-sky-600 to-blue-600 text-white font-semibold rounded-lg hover:from-sky-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isLogin ? "Logging in..." : "Registering..."}
                </span>
              ) : isLogin ? (
                "Login"
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {/* Forgot Password (only for login) */}
          {isLogin && (
            <div className="text-right mt-4">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  showToast("Redirecting to password reset...", "info");
                  // You can add navigation to forgot password page here
                }}
                className="text-sm text-sky-600 hover:text-sky-800 transition-colors"
              >
                Forgot Password?
              </button>
            </div>
          )}

          {/* Toggle between Login and Register */}
          <div className="text-center mt-6 pt-4 border-t border-gray-200">
            <p className="text-gray-600">
              {isLogin ? "Don't have an account?" : "Already a member?"}{" "}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setErrors({});
                  setTouched({});
                  setFormData({
                    username: "",
                    email: "",
                    password: "",
                    confirmPassword: "",
                    phone: "",
                    gender: "",
                    hometowncity: "",
                    DOB: "",
                    Aniversary: "",
                    refrence: "",
                  });
                  setPhoneNumber("");
                  setSelectedCountry(countryCodes[0]);
                }}
                className="text-sky-600 font-semibold hover:text-sky-700 hover:underline transition-colors"
              >
                {isLogin ? "Create Account" : "Login"}
              </button>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Email Verification Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        email={registeredEmail}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </div>
  );
};

export default LoginModal;
